import { z } from 'zod';

const dailyPointSchema = z.object({
  date: z.string(),
  count: z.number(),
});

export const dashboardTrendsDataSchema = z.object({
  from: z.string(),
  to: z.string(),
  series: z.object({
    orders: z.array(dailyPointSchema),
    jobs: z.array(dailyPointSchema),
    tasks: z.array(dailyPointSchema),
    shipments: z.array(dailyPointSchema),
    quotes: z.array(dailyPointSchema),
    asset_instances: z.array(dailyPointSchema),
  }),
});

export const orderStatsSchema = z.object({
  total: z.number(),
  pending: z.number(),
  confirmed: z.number(),
  in_progress: z.number(),
  completed: z.number(),
  cancelled: z.number(),
  on_hold: z.number(),
  overdue: z.number(),
});

export const jobStatsSchema = z.object({
  total: z.number(),
  draft: z.number(),
  assigned: z.number(),
  in_progress: z.number(),
  completed: z.number(),
  cancelled: z.number(),
  on_hold: z.number(),
  overdue: z.number(),
  byType: z.object({
    delivery: z.number(),
    pickup: z.number(),
    installation: z.number(),
    maintenance: z.number(),
    transfer: z.number(),
  }),
  byPriority: z.object({
    low: z.number(),
    normal: z.number(),
    high: z.number(),
    urgent: z.number(),
  }),
});

export const taskStatsSchema = z.object({
  total: z.number(),
  pending: z.number(),
  assigned: z.number(),
  in_progress: z.number(),
  completed: z.number(),
  blocked: z.number(),
  cancelled: z.number(),
  overdue: z.number(),
  my_tasks: z.number().optional(),
  byType: z.record(z.string(), z.number()),
  byPriority: z.object({
    low: z.number(),
    normal: z.number(),
    high: z.number(),
    urgent: z.number(),
  }),
});

export const quoteStatsSchema = z.object({
  total: z.number(),
  draft: z.number(),
  ready: z.number(),
  sent: z.number(),
  viewed: z.number(),
  accepted: z.number(),
  rejected: z.number(),
  expired: z.number(),
  converted: z.number(),
  total_value: z.number(),
  avg_value: z.number(),
});

export const shipmentStatsSchema = z.object({
  total: z.number(),
  active: z.number(),
  delivered: z.number(),
  overdue: z.number(),
  byType: z.object({
    inbound: z.number(),
    outbound: z.number(),
    transfer: z.number(),
  }),
});

export const qrCodeStatsSchema = z.object({
  total: z.number(),
  assigned: z.number(),
  unassigned: z.number(),
  assignedPercentage: z.number(),
  totalScans: z.number(),
  avgScans: z.string(),
  byStatus: z.object({
    generated: z.number(),
    printed: z.number(),
    assigned: z.number(),
    active: z.number(),
    damaged: z.number(),
  }),
});

export const assetInstanceStatisticsSchema = z.object({
  total: z.number(),
  available: z.number(),
  rented: z.number(),
  maintenance: z.number(),
  damaged: z.number(),
  utilization_rate: z.number(),
});

export const assetTemplateStatsSchema = z.object({
  totalUnits: z.number(),
  totalTemplates: z.number(),
  totalActiveTemplates: z.number(),
  totalRentedUnits: z.number(),
});

export const assetCategoryStatsSchema = z.object({
  total: z.number(),
  active: z.number(),
  totalProducts: z.number(),
  empty: z.number(),
});

export const supplierStatsSchema = z.object({
  total: z.number(),
  active: z.number(),
  linkedProducts: z.number(),
});

export type QrCodeStats = z.infer<typeof qrCodeStatsSchema>;
export type AssetInstanceStatistics = z.infer<typeof assetInstanceStatisticsSchema>;
export type AssetTemplateStats = z.infer<typeof assetTemplateStatsSchema>;
export type AssetCategoryStats = z.infer<typeof assetCategoryStatsSchema>;
export type SupplierStats = z.infer<typeof supplierStatsSchema>;
